import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { roadSeries } from '../model/forecast';
import { intersectionTypeLabel } from '../model/catalog';
import { vcColor, vcLabel } from '../model/capacity';
import { SCORE_SCALE_BEST, SCORE_SCALE_WORST } from '../../../constants/scoreScale';
import { cycleLength } from '../model/signals';
import IntersectionDiagram from './IntersectionDiagram';

function Kpi({ value, label, color }) {
  return (
    <div>
      <b style={color ? { color } : undefined}>{value}</b>
      <span>{label}</span>
    </div>
  );
}

function SignalTimeline({ maneuvers, plan, optimized }) {
  const source = optimized || plan;
  const cycle = Number(source?.cycle || cycleLength(source) || 90);
  const phases = optimized?.phases?.length
    ? optimized.phases
    : maneuvers.map((maneuver) => ({
      maneuverId: maneuver.id,
      nome: maneuver.title,
      green: Number(source?.green || 30),
      yellow: Number(source?.yellow || 3),
      red: Number(source?.red || Math.max(0, cycle - 33)),
    }));
  return (
    <div className="semafix-plan-table-wrap">
      <div className="semafix-plan-title">
        <b>{optimized ? 'Piano semaforico ottimizzato' : 'Piano semaforico attuale'}</b>
        <span>Durata ciclo: {cycle} s</span>
      </div>
      <table className="semafix-plan-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Manovra</th>
            <th>0</th>
            <th>Verde</th>
            <th>Giallo</th>
            <th>Rosso</th>
          </tr>
        </thead>
        <tbody>
          {phases.map((phase, index) => {
            const green = Number((phase.green ?? phase.verde ?? source?.green) || 0);
            const yellow = Number((phase.yellow ?? source?.yellow) || 0);
            const red = Number(phase.red ?? Math.max(0, cycle - green - yellow));
            return (
              <tr key={phase.maneuverId || phase.nome || index}>
                <td>M{index + 1}</td>
                <td>{phase.nome || maneuvers[index]?.title || `Manovra ${index + 1}`}</td>
                <td>
                  <div className="semafix-plan-track" aria-label={`${green}s verde, ${yellow}s giallo, ${red}s rosso`}>
                    <i className="green" style={{ width: `${(green / cycle) * 100}%` }} />
                    <i className="yellow" style={{ width: `${(yellow / cycle) * 100}%` }} />
                    <i className="red" style={{ width: `${(red / cycle) * 100}%` }} />
                  </div>
                </td>
                <td>{green}</td>
                <td>{yellow}</td>
                <td>{red}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="semafix-plan-key">
        <span><i className="green" />Tempo di verde</span>
        <span><i className="yellow" />Tempo di giallo</span>
        <span><i className="red" />Tempo di rosso</span>
      </div>
    </div>
  );
}

function PopupShell({ title, subtitle, onClose, children, wide }) {
  return (
    <div className="semafix-modal-layer">
      <div className={`semafix-popup ${wide ? 'semafix-popup-wide' : ''}`}>
        <button className="semafix-popup-close" onClick={onClose} aria-label="Chiudi">
          <CloseOutlined />
        </button>
        <div className="semafix-popup-header">
          <div className="semafix-popup-title">{title}</div>
          <div className="semafix-popup-subtitle">
            <span className="semafix-dot" />
            {subtitle}
          </div>
        </div>
        <div className="semafix-popup-body">{children}</div>
      </div>
    </div>
  );
}

export function SegmentPanel({ road, flow, vc, onClose }) {
  if (!road) return null;
  // §14: the flow is the observed value where a camera/monitoring is present,
  // otherwise the estimated value for the current time of day.
  const observed = Boolean(road.monitored || road.camera || road.observed);
  return (
    <PopupShell
      title={road.n}
      subtitle="ANALISI TRAFFICO · CONDIZIONE ATTUALE"
      onClose={onClose}
    >
      <div className="semafix-kpis semafix-kpis-3">
        <Kpi value={flow} label={observed ? 'Flusso reale · veicoli/ora' : 'Flusso stimato · veicoli/ora'} />
        <Kpi value={road.cap} label="Capacità · veicoli/ora" />
        <Kpi value={vc.toFixed(2).replace('.', ',')} label="V/C attuale" color={vcColor(vc)} />
      </div>

      <div className="semafix-road-chart-head">
        <b>Flusso veicolare</b>
        <span><i className="observed" />{observed ? 'Flusso osservato' : 'Flusso stimato'}</span>
        <span><i className="forecast" />Flusso previsto</span>
        <span><i className="capacity" />Capacità</span>
      </div>
      <div className="semafix-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={roadSeries(road)} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#999' }} stroke="#e8e8e8" />
            <YAxis tick={{ fontSize: 9, fill: '#999' }} stroke="#e8e8e8" />
            <Tooltip />
            {/* Capacity reference so it is clear when flow approaches/exceeds it. */}
            <ReferenceLine
              y={road.cap}
              stroke={SCORE_SCALE_WORST}
              strokeDasharray="4 4"
              label={{ value: 'Capacità', position: 'insideTopRight', fontSize: 9, fill: SCORE_SCALE_WORST }}
            />
            {/* Current time marker splitting past/observed from forecast. */}
            <ReferenceLine
              x="ORA"
              stroke="#8c8c8c"
              strokeDasharray="2 3"
              label={{ value: 'ora', position: 'top', fontSize: 9, fill: '#8c8c8c' }}
            />
            <Line type="monotone" dataKey="observed" name={observed ? 'Osservato' : 'Stimato'} stroke="#2f80ed" strokeWidth={2.5} dot={false} connectNulls={false} />
            <Line type="monotone" dataKey="forecast" name="Previsione" stroke="#2f80ed" strokeWidth={2.5} strokeDasharray="5 4" dot={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="semafix-flow-source">
        <span className="semafix-flow-info">i</span>
        <span>
          <b>{observed ? 'Flusso reale da telecamera' : 'Flusso stimato dal modello'}</b>
          <small>Condizione corrente: <em style={{ color: vcColor(vc) }}>{vcLabel(vc)}</em></small>
        </span>
      </div>
    </PopupShell>
  );
}

export function IntersectionPanel({
  intersection, signal, arms, plan, optimized, crossings,
  maneuvers,
  onOpenEditor, onClose,
}) {
  if (!intersection) return null;
  const cycle = cycleLength(plan);
  const worst = arms.reduce((max, arm) => Math.max(max, arm.vc), 0);
  const pedestrianMinimum = crossings.reduce((max, item) => Math.max(max, item.minGreen), 0);

  return (
    <PopupShell
      title={signal?.name || intersection.name || intersectionTypeLabel(intersection.type)}
      subtitle={`${intersectionTypeLabel(intersection.type).toUpperCase()} · ${arms.length} BRACCI`}
      onClose={onClose}
      wide
    >
      <div className="semafix-kpis">
        <Kpi value={arms.length} label="Bracci" />
        <Kpi value={arms.reduce((sum, arm) => sum + arm.flow, 0)} label="Flusso v/h" />
        <Kpi value={worst.toFixed(2).replace('.', ',')} label="V/C max" color={vcColor(worst)} />
        <Kpi value={signal ? `${cycle}s` : '—'} label="Ciclo" />
      </div>

      <div className="semafix-sect">Manovre e attraversamenti</div>
      <IntersectionDiagram
        intersection={intersection}
        maneuvers={maneuvers}
        crossings={crossings}
        onOpenEditor={onOpenEditor}
      />

      {signal && (
        <>
          <div className="semafix-signal-parameters">
            <div><span>Durata ciclo</span><b>{optimized?.cycle || cycle} s</b></div>
            <div><span>Tempo di giallo</span><b>{optimized?.yellow || plan.yellow} s</b></div>
            <div><span>Tempo di rosso generale</span><b>{optimized?.allRed || 2} s</b></div>
          </div>
          {pedestrianMinimum > 0 && (
            <div className="semafix-constraint">Vincolo pedonale: verde minimo {pedestrianMinimum} s</div>
          )}
          <SignalTimeline maneuvers={maneuvers} plan={plan} optimized={optimized} />
        </>
      )}
    </PopupShell>
  );
}

export function PedestrianPanel({ crossing, onClose }) {
  if (!crossing) return null;
  const adult = (crossing.length / 1.2 + 3).toFixed(1);
  const slow = (crossing.length / 0.8 + 4).toFixed(1);
  const safe = crossing.currGreen >= crossing.minGreen;
  return (
    <PopupShell
      title={crossing.name}
      subtitle={`ATTRAVERSAMENTO · RISCHIO ${String(crossing.risk).toUpperCase()}`}
      onClose={onClose}
    >
      <div className="semafix-kpis">
        <Kpi value={`${crossing.length}m`} label="Lunghezza" />
        <Kpi value={`${adult}s`} label="Tempo adulto" />
        <Kpi value={`${slow}s`} label="Utenza lenta" />
        <Kpi value={`${crossing.currGreen}s`} label="Verde attuale" color={safe ? SCORE_SCALE_BEST : SCORE_SCALE_WORST} />
      </div>
      <div className="semafix-sect">Sicurezza pedonale</div>
      <div className="semafix-rows">
        <div><span>Flusso pedonale</span><b>{crossing.pedH} ped/h</b></div>
        <div><span>Verde minimo richiesto</span><b>{crossing.minGreen} s</b></div>
        <div><span>Stato</span><b style={{ color: safe ? SCORE_SCALE_BEST : SCORE_SCALE_WORST }}>{safe ? 'Adeguato' : 'Da incrementare'}</b></div>
      </div>
    </PopupShell>
  );
}
